import React, { useEffect, useState } from 'react';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Input } from "../../../components/DesignSystem/components/Input";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { Chip } from "../../../components/DesignSystem/components/Chip";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, TextField, CircularProgress, LinearProgress } from "@mui/material";
import { API_URL } from "../../../api";
import * as userActions from "../../../actions/user";
import { useCookies } from "../../../hooks/useCookies"; 
import { useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import log from "../../../utils/log";
import { useGeolocation } from "../../../utils/geolocationUtils";
import { countries } from "country-list-json";
import Loader from "../../../components/Utility/Loader";
import locationBg from "../../../assets/images/img_login.png";
export const LocationSelection = () => {
    const navigate = useNavigate();
    
    // 1. Theme Configuration Hook
    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // 2. FOUC Tracker State
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    // 3. Centralized FOUC Prevention & Engine Mount
    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "action-black": "#111111",
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                            },
                        },
                    },
                };
            }
        };

        if (window.tailwind) {
            applyConfig();
            setIsTailwindLoaded(true);
        } else {
            const script = document.createElement("script");
            script.src = "https://cdn.tailwindcss.com?plugins=forms";
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };
            document.head.appendChild(script);
        }
    }, [mode]);

    const isFetched = useRef(false);
    const { getCookie } = useCookies();
    const token = getCookie("token");

    const [options, setOptions] = useState({});
    const [countryOptions, setCountryOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [loadingCountries, setLoadingCountries] = useState(true);
    const [loadingCities, setLoadingCities] = useState(false);
    const [errorMessageCountry, setErrorMessageCountry] = useState("");
    const [errorMessageCity, setErrorMessageCity] = useState("");
    const [selectedCityId, setSelectedCityId] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const { detectLocation } = useGeolocation();
    const [isDetecting, setIsDetecting] = useState(false);
    const autoDetectRef = useRef(false);

    const triggerAutoDetect = async (currentOptions) => {
        if (autoDetectRef.current) return;
        autoDetectRef.current = true;

        // 1. Pre-fill Country from Signup (localStorage)
        let activeCountryKey = null;
        let activeCityOptions = [];
        const savedCountry = localStorage.getItem("signup_country");
        
        if (savedCountry) {
            activeCountryKey = Object.keys(currentOptions).find(key => 
                key.toLowerCase().trim() === savedCountry.toLowerCase().trim() ||
                savedCountry.toLowerCase().trim().includes(key.toLowerCase().trim()) ||
                key.toLowerCase().trim().includes(savedCountry.toLowerCase().trim())
            );
            
            if (activeCountryKey) {
                console.log("Pre-filling country from Signup:", activeCountryKey);
                setSelectedCountry(activeCountryKey);
                
                activeCityOptions = (currentOptions[activeCountryKey] || []).map((city) => ({
                    label: city.location_string,
                    id: city.id,
                }));
                setCityOptions(activeCityOptions);
            }
        }

        console.log("Checking geolocation permissions for auto-detect...");
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const status = await navigator.permissions.query({ name: 'geolocation' });
                if (status.state === 'denied') {
                    console.warn("Location permission is denied by browser.");
                    setIsDetecting(false);
                    return; // Stop detection, relying on manual entry (and prepopulated country if any)
                }
            } catch (err) {
                console.warn("Permissions query failed, proceeding with detection attempt...");
            }
        }

        setIsDetecting(true);
        console.log("Auto-detection UI triggered.");
        
        // 5-second timeout window
        const detectionTimeout = new Promise((_, reject) => {
            setTimeout(() => {
                reject(new Error("Detection timed out"));
            }, 5000);
        });

        try {
            console.log("Starting detection promise race...");
            const locationData = await Promise.race([detectLocation(), detectionTimeout]);
            
            if (locationData) {
                console.log("Resolved locationData:", locationData);

                // If geolocation found a country, we prefer it over the Signup one (more accurate)
                if (locationData.countryCode) {
                    const countryData = countries.find(c => c.code.toLowerCase() === locationData.countryCode.toLowerCase());
                    const countryName = countryData?.name;
                    
                    if (countryName) {
                        const matchedGeoCountry = Object.keys(currentOptions).find(key => 
                            key.toLowerCase().trim() === countryName.toLowerCase().trim() ||
                            countryName.toLowerCase().trim().includes(key.toLowerCase().trim()) ||
                            key.toLowerCase().trim().includes(countryName.toLowerCase().trim())
                        );

                        if (matchedGeoCountry) {
                            activeCountryKey = matchedGeoCountry;
                            setSelectedCountry(activeCountryKey);
                            activeCityOptions = (currentOptions[activeCountryKey] || []).map((city) => ({
                                label: city.location_string,
                                id: city.id,
                            }));
                            setCityOptions(activeCityOptions);
                        }
                    }
                }

                // If we have an active country (either from Signup or Geolocation), try to set City
                if (activeCountryKey && locationData.city) {
                    const detectedCityLower = locationData.city.toLowerCase().trim();
                    const matchedCity = activeCityOptions.find(c => 
                        c.label.toLowerCase().trim() === detectedCityLower ||
                        c.label.toLowerCase().trim().includes(detectedCityLower) ||
                        detectedCityLower.includes(c.label.toLowerCase().trim())
                    );

                    if (matchedCity) {
                        console.log("Matched detectable city from backend list:", matchedCity.label);
                        setSelectedCity(matchedCity.label);
                        setSelectedCityId(matchedCity.id);
                        setIsDetecting(false);
                        return; // Successfully detected both
                    }
                }

                // If geolocation returned a valid country from the backend list but failed the city match
                if (activeCountryKey && locationData.countryCode) {
                    setIsDetecting(false);
                    return;
                }
            }
            alert("unable to detect it, pls enter manually");
        } catch (error) {
            console.error("Auto-detect failed:", error.message);
            alert("unable to detect it, pls enter manually");
        } finally {
            setIsDetecting(false);
        }
    };


    useEffect(() => {
        if (!token) {
            console.warn("Token not available in LocationSelection yet.");
            return;
        }
        if (isFetched.current) return;
        isFetched.current = true;
        
        const fetchOptions = async () => {
            console.log("Fetching location options from backend...");
            setLoadingCountries(true);
            try {
                const data = await userActions.getCountryOptions();
                if (data && typeof data === 'object' && Object.keys(data).length > 0) {
                    console.log("Location options received:", Object.keys(data).length, "countries found.");
                    setOptions(data);
                    const countryList = Object.keys(data);
                    setCountryOptions(countryList);
                    
                    // Trigger auto-detect after dropdown data is ready
                    triggerAutoDetect(data);
                } else {
                   console.error("Backend returned empty location data object.");
                   setErrorMessageCountry("Could not load location data. Please refresh.");
                }
            } catch (error) {
                console.error("Failed to fetch location options from API:", error);
                isFetched.current = false; // Allow retry on next render if failed
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchOptions();
    }, [token]);

    const handleCountryChange = (event, value) => {
        setSelectedCountry(value);
        setSelectedCity(null);
        setSelectedCityId(null);
        if (value && options[value]) {
            const cities = options[value].map((city) => ({
                label: city.location_string,
                id: city.id,
            }));
            setCityOptions(cities);
        } else {
            setCityOptions([]);
        }
        setErrorMessageCountry("");
    };

    const handleCityChange = (event, value) => {
        if (value) {
            setSelectedCity(value.label);
            setSelectedCityId(value.id);
        } else {
            setSelectedCity(null);
            setSelectedCityId(null);
        }
        setErrorMessageCity("");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        if (!selectedCountry) {
            setErrorMessageCountry("*Required");
            setLoading(false);
            return;
        }

        if (!selectedCityId) {
            setErrorMessageCity("*Please select from the list");
            setLoading(false);
            return;
        }

        fetch(`${API_URL}customer/users/locations`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ location_id: selectedCityId }),
        })
        .then((response) => response.json())
        .catch((error) => {
            console.error("Error saving location:", error);
        });

        // Navigate instantly for a smooth transition
        setLoading(false);
        navigate("/selfie");
    };

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased min-h-[100dvh] lg:h-screen lg:overflow-hidden w-full`}
            style={{ opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            {isDetecting && (
                <div className="fixed top-0 left-0 w-full z-[100]">
                    <LinearProgress color="warning" />
                </div>
            )}
            <div className="flex flex-col lg:flex-row min-h-[100dvh] lg:h-screen w-full overflow-hidden">
                
                {/* ---------- LEFT SIDE: HERO/VISUAL ---------- */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden h-full">
                    <div className="absolute inset-0 bg-brand/10 mix-blend-multiply z-10"></div>
                    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat w-full h-full" style={{ backgroundImage: `url(${locationBg})` }}></div>
                    
                    <div className="absolute bottom-12 left-12 z-20 max-w-md">
                        <Heading variant="h2" className="login-hero-title mb-[var(--ds-space-4)]">Find matches near you.</Heading>
                        <Text as="p" className="ds-text-hero-subtitle">Connect with authentic people in your local area.</Text>
                    </div>
                </div>
                
                {/* ---------- RIGHT SIDE: CONTENT / FORM ---------- */}
                <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-background-dark min-h-[100dvh] lg:h-screen overflow-y-auto custom-scrollbar relative">
                    
                    <header className="sticky top-0 z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md px-[var(--ds-space-6)] py-[var(--ds-space-4)] flex items-center justify-between border-b border-brand/10">
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Go back">
                            <ArrowLeft className="shrink-0" size={24} />
                        </button>
                        <div className="flex items-center gap-[var(--ds-space-2)]">
                            <div className="h-2 w-24 bg-brand/20 rounded-full overflow-hidden">
                                <div className="h-full bg-brand" style={{ width: '33%' }}></div>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-brand">Step 2 of 6</span>
                        </div>
                        <button type="button" className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Close">
                            <X className="shrink-0" size={24} />
                        </button>
                    </header>

                    <main className="flex-1 px-[var(--ds-space-6)] py-[var(--ds-space-6)] lg:px-[var(--ds-space-12)] lg:py-[var(--ds-space-12)] max-w-2xl mx-auto w-full flex flex-col relative pb-10 lg:pb-[var(--ds-space-12)]">
                        {isDetecting && (
                            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-brand/10 px-3 py-1 rounded-full border border-brand/20 animate-pulse">
                                <CircularProgress size={12} thickness={5} color="warning" />
                                <span className="text-[10px] font-bold text-brand uppercase tracking-widest">Detecting Location...</span>
                            </div>
                        )}

                        <div className="mb-[var(--ds-space-8)]">
                            <Heading variant="h1" className="ds-heading-page-title text-slate-900 dark:text-slate-100 mb-[var(--ds-space-2)]">
                                Where are you located?
                            </Heading>
                            <Text as="p" className="ds-text-page-subtitle text-slate-500 dark:text-slate-400">Discover potential matches nearby.</Text>
                        </div>

                        <form id="location-form" className="flex flex-col flex-1 gap-[var(--ds-space-8)]" onSubmit={handleSubmit}>
                            <div className="flex flex-col gap-[var(--ds-space-6)]">
                                {/* Country Select */}
                                <div className="flex flex-col gap-[var(--ds-space-2)]">
                                    <Text as="label" className="ds-label-form text-slate-400 ml-1 mb-1">Country <span className="text-red-500">*</span></Text>
                                    <Autocomplete
                                        options={countryOptions}
                                        value={selectedCountry || null}
                                        onChange={handleCountryChange}
                                        loading={loadingCountries}
                                        autoHighlight
                                        isOptionEqualToValue={(option, value) => option === value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={loadingCountries ? "Loading..." : "Select Country"}
                                                variant="outlined"
                                                error={Boolean(errorMessageCountry)}
                                                sx={{
                                                    backgroundColor: mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "#f8fafc",
                                                    borderRadius: "16px",
                                                    border: mode === "dark" ? "1px solid rgba(51, 65, 85, 0.5)" : "1px solid #e2e8f0",
                                                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                    "& .MuiInputBase-input": { padding: "10px", color: mode === "dark" ? "white" : "black" },
                                                }}
                                            />
                                        )}
                                    />
                                    {errorMessageCountry && <span className="text-red-500 text-xs ml-1">{errorMessageCountry}</span>}
                                </div>

                                {/* City Select */}
                                {selectedCountry && (
                                <div className="flex flex-col gap-[var(--ds-space-2)]">
                                    <Text as="label" className="ds-label-form text-slate-400 ml-1 mb-1">City/Province <span className="text-red-500">*</span></Text>
                                    <Autocomplete
                                        options={cityOptions}
                                        value={selectedCity ? { label: selectedCity, id: selectedCityId } : null}
                                        onChange={handleCityChange}
                                        loading={loadingCities}
                                        autoHighlight
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>{option.label}</li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Select City/Province"
                                                variant="outlined"
                                                error={Boolean(errorMessageCity)}
                                                sx={{
                                                    backgroundColor: mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "#f8fafc",
                                                    borderRadius: "16px",
                                                    border: mode === "dark" ? "1px solid rgba(51, 65, 85, 0.5)" : "1px solid #e2e8f0",
                                                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                    "& .MuiInputBase-input": { padding: "10px", color: mode === "dark" ? "white" : "black" },
                                                }}
                                            />
                                        )}
                                    />
                                    {errorMessageCity && <span className="text-red-500 text-xs ml-1">{errorMessageCity}</span>}
                                </div>
                                )}
                            </div>

                            {/* Sticky CTA for Mobile */}
                            <div className="fixed bottom-0 left-0 right-0 z-[40] px-[var(--ds-space-6)] py-4 bg-white/90 dark:bg-background-dark/95 backdrop-blur-lg border-t border-slate-200 dark:border-slate-800 lg:static lg:bg-transparent lg:backdrop-blur-none lg:p-0 lg:border-none lg:z-auto lg:mt-auto lg:pt-[var(--ds-space-2)]">
                                <DSButton 
                                    type="submit"
                                    variant="cta"
                                    endIcon={!loading && <ArrowRight size={20} />}
                                    disabled={loading}
                                    className="w-full"
                                >
                                    {loading ? <Spinner animation="border" size="sm" /> : "Continue"}
                                </DSButton>
                            </div>
                        </form>
                    </main>

                    <footer className="mt-auto p-[var(--ds-space-6)] pb-32 lg:pb-[var(--ds-space-6)] text-center">
                        <Text as="p" className="ds-text-disclaimer text-slate-400">By continuing, you agree to our Terms of Service and Privacy Policy.</Text>
                    </footer>
                </div>
            </div>
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </div>
    );
};
